<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  totalCount: { type: Number, default: null },
  buttonLabel: { type: String, default: '' },
  newContactLabel: { type: String, default: '' },
  consentLabel: { type: String, default: '' },
  mapLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'exports',
  'imports',
  'migration',
  'bulkLabelByPhone',
  'createSegment',
  'deleteSegment',
  'new-contact',
  'consent',
  'map',
]);

const headerDescription =
  'Encontre, organize e acompanhe todos os seus contatos.';
</script>

<template>
  <header
    class="sticky top-0 z-20 border-b border-n-weak bg-n-solid-1/95 px-4 backdrop-blur sm:px-6 lg:px-8"
  >
    <div
      class="mx-auto flex w-full max-w-[90rem] flex-col gap-4 py-5 2xl:flex-row 2xl:items-center 2xl:justify-between"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-hugeicons-contact-book" class="size-5" />
        </span>
        <div class="min-w-0">
          <div class="flex min-w-0 items-center gap-2">
            <h1 class="mb-0 truncate text-xl font-semibold text-n-slate-12">
              {{ headerTitle }}
            </h1>
            <span
              v-if="totalCount !== null"
              class="shrink-0 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium tabular-nums text-n-slate-11"
            >
              {{
                $t(
                  'CONTACTS_LAYOUT.LIST.COUNT',
                  { count: totalCount.toLocaleString('pt-BR') },
                  totalCount
                )
              }}
            </span>
          </div>
          <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
            {{ headerDescription }}
          </p>
        </div>
      </div>
      <div
        class="flex min-w-0 flex-col gap-3 md:flex-row md:items-center 2xl:flex-1 2xl:justify-end"
      >
        <div
          v-if="showSearch"
          class="flex w-full min-w-0 items-center md:flex-1 2xl:max-w-sm"
        >
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="['h-9 bg-n-solid-1 ltr:!pl-9 !py-1 rtl:!pr-9']"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="pointer-events-none absolute top-1/2 z-10 size-4 -translate-y-1/2 !text-n-slate-10 dark:!text-n-slate-9 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div
          class="flex flex-wrap items-center gap-2 md:shrink-0 md:flex-nowrap"
        >
          <div
            class="flex items-center gap-1 rounded-xl border border-n-weak p-1"
          >
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                :icon="
                  isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
                "
                color="slate"
                size="sm"
                class="relative w-8"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
              @exports="emit('exports')"
              @imports="emit('imports')"
              @migration="emit('migration')"
              @bulk-label-by-phone="emit('bulkLabelByPhone')"
            />
          </div>
          <Button
            v-if="mapLabel"
            :label="mapLabel"
            size="sm"
            color="slate"
            variant="outline"
            icon="i-lucide-map"
            @click="emit('map')"
          />
          <Button
            v-if="consentLabel"
            :label="consentLabel"
            size="sm"
            color="slate"
            variant="outline"
            icon="i-lucide-shield-check"
            @click="emit('consent')"
          />
          <Button
            v-if="newContactLabel"
            :label="newContactLabel"
            size="sm"
            color="slate"
            variant="outline"
            icon="i-lucide-user-plus"
            @click="emit('new-contact')"
          />
          <ComposeConversation>
            <template #trigger>
              <Button :label="buttonLabel" size="sm" />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
